import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { authErrorMessage, isAccountAlreadyRegistered } from "@/lib/auth-error";
import { useLanguage } from "@/contexts/LanguageContext";

export function SignupPage() {
  const { signUp } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [fullName, setFullName] = React.useState("");
  const [organizationName, setOrganizationName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setRegisteredEmail(null);
    setIsSubmitting(true);
    try {
      const result = await signUp({ fullName, organizationName, email, password });
      navigate(result.requiresEmailConfirmation ? "/confirm-email" : "/dashboard", {
        replace: true,
        state: result.requiresEmailConfirmation ? { email } : undefined,
      });
    } catch (err) {
      if (isAccountAlreadyRegistered(err)) {
        setRegisteredEmail(email.trim());
        setError(t("This email is already registered. Sign in or recover your password."));
      } else {
        setError(authErrorMessage(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Create your organization</h1>
        <p className="text-sm text-muted-foreground">
          Start your workspace — you can invite your team and add weddings right after.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="organizationName">Organization name</Label>
          <Input
            id="organizationName"
            required
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            placeholder="Rivera & Co. Weddings"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="fullName">Your name</Label>
          <Input
            id="fullName"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Alex Rivera"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Work email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@studio.com"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {registeredEmail && (
          <Link to="/forgot-password" state={{ email: registeredEmail }} className="text-sm font-medium text-primary hover:underline">
            {t("Recover password")}
          </Link>
        )}

        <Button type="submit" disabled={isSubmitting} className="mt-1">
          {isSubmitting && <LoadingSpinner className="text-primary-foreground" />}
          Create organization
        </Button>
      </form>

      <p className="text-sm text-muted-foreground">
        Already have a workspace?{" "}
        <Link to="/login" className="font-medium text-primary hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
